/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  Tab,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  TabsSkeleton
} from '@carbon/react';
import _ from 'lodash';
import React, { useCallback, useState, useEffect } from 'react';

interface HeaderTabsProps {
  /**
   *
   */
  assistantId: string;
  /**
   *
   */
  environmentId: string;
  /**
   *
   */
  catalogItemId?: string;
  /**
   *
   */
  Tabs: JSX.Element[];
  /**
   *
   */
  loading: boolean;
  /**
   *
   */
  label: string;
  /**
   *
   */
  onClickTab: (index: number) => void;
  /**
   *
   */
  defaultTab?: number;
  /**
   *
   */
  readOnly: boolean;
}
export const HeaderTabs = ({
  Tabs: HeaderTabs,
  assistantId,
  environmentId,
  catalogItemId,
  loading,
  onClickTab,
  defaultTab,
  readOnly
}: HeaderTabsProps) => {
  const [renderedTabs, setRenderedTabs] = useState<JSX.Element[]>([]);
  const [renderedTabPanels, setRenderedTabPanels] = useState<JSX.Element[]>([]);

  const [selectedTab, setSelectedTab] = useState<number>(
    Math.min(defaultTab ?? 0, renderedTabs.length)
  );

  /**
   *
   */
  const renderChildrenIfSelected = useCallback(
    ({ selected, children, _className, id, ...other }) => (
      <div role="tabpanel" id={id} className="bx--tab-content" {...other}>
        {selected && children}
      </div>
    ),
    []
  );

  const handleOnClick = selectedIndex => {
    setSelectedTab(selectedIndex);
    onClickTab(selectedIndex);
  };

  /**
   * Internal array of Tabs and Content.
   */
  useEffect(() => {
    setRenderedTabs(
      HeaderTabs.map((tabContent, index) => {
        const id = _.snakeCase(tabContent.props?.tabname) ?? index;
        return (
          tabContent && <Tab>{tabContent.props.tabname || `tab-${id}`}</Tab>
        );
      })
    );
  }, [HeaderTabs]);

  useEffect(() => {
    setRenderedTabPanels(
      HeaderTabs.map((tabContent, index) => {
        const id = _.snakeCase(tabContent.props?.tabname) ?? index;
        if (tabContent) {
          return (
            <TabPanel
              className="TabbedHeader__tab-name"
              aria-label={`tab-${id}`}
              id={`tab-${id}`}
              key={`key-${id}`}
              onClick={() => onClickTab(index)}
              onKeyDown={() => onClickTab(index)}>
              <div className="TabbedHeader__container bx--grid bx--no-gutter">
                {React.cloneElement(tabContent, {
                  assistantId,
                  environmentId,
                  catalogItemId,
                  readOnly
                })}
              </div>
            </TabPanel>
          );
        } else {
          // This needs to be set as a react element otherwise UI crashes
          return <></>;
        }
      })
    );
  }, [
    assistantId,
    catalogItemId,
    environmentId,
    onClickTab,
    readOnly,
    renderChildrenIfSelected,
    HeaderTabs
  ]);

  // NOTE that in the Tabs component, the Math.min function is used
  // so the selectedTab index does not go out of bounds in relation
  // to renderedTabs.length.
  return (
    <section className="bx--grid Panel--Tabs-Grid">
      <div className="bx--row">
        <div className="bx--col bx--col-sm-4 bx--col-md-8 bx--col-lg-16 bx--col-xlg-16">
          {loading ? (
            <TabsSkeleton />
          ) : (
            <Tabs
              selectedIndex={selectedTab}
              onChange={tab => handleOnClick(tab.selectedIndex)}>
              <TabList aria-label="header-tabs" contained>
                {renderedTabs}
              </TabList>
              <TabPanels>{renderedTabPanels}</TabPanels>
            </Tabs>
          )}
        </div>
      </div>
    </section>
  );
};
